import React, { useState, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  XMarkIcon, 
  CheckCircleIcon, 
  ExclamationTriangleIcon, 
  InformationCircleIcon, 
  XCircleIcon 
} from '@heroicons/react/24/outline'
import { VariantReader } from 'aldoo-ra/VariantReader'
import { twMerge } from 'tailwind-merge'
import LocalUserPreferences from 'aldoo-ra/LocalUserPreferences'

// Position constants
export const TOAST_POSITIONS = {
  TOP_LEFT: 'TOP_LEFT',
  TOP_CENTER: 'TOP_CENTER',
  TOP_RIGHT: 'TOP_RIGHT',
  BOTTOM_LEFT: 'BOTTOM_LEFT',
  BOTTOM_RIGHT: 'BOTTOM_RIGHT'
}

// Toast component
const Toast = ({
  id,
  title,
  message,
  icon,
  variant = 'default',
  duration = 5000,
  persistent = false,
  onClose,
  children,
  className = '',
  styleOverrides,
  position = TOAST_POSITIONS.TOP_RIGHT
}) => {
  const [isVisible, setIsVisible] = useState(true)
  const [isPaused, setIsPaused] = useState(false)
  const [progressWidth, setProgressWidth] = useState(100)
  const timeoutRef = useRef(null)
  const startTimeRef = useRef(Date.now())
  const remainingTimeRef = useRef(duration)

  const styles = VariantReader.useVariant({
    componentName: 'Toast',
    variant,
    styleOverrides,
  })

  // Toast positioning is now handled by ToastContainer
  const getPositionClasses = () => {
    return 'relative' // Remove fixed positioning since container handles it
  }

  const getDefaultIcon = () => {
    switch (variant) {
      case 'success':
      case 'admin_success':
        return CheckCircleIcon
      case 'error':
      case 'admin_error':
        return XCircleIcon
      case 'warning':
      case 'admin_warning':
        return ExclamationTriangleIcon
      case 'info':
      case 'admin_info':
        return InformationCircleIcon
      default:
        return InformationCircleIcon
    }
  }

  const IconComponent = icon || getDefaultIcon()

  useEffect(() => {
    if (persistent || isPaused) return

    const startTimer = () => {
      // Set progress to 0% to trigger animation
      setProgressWidth(0)
      
      timeoutRef.current = setTimeout(() => {
        handleClose()
      }, remainingTimeRef.current)
    }

    startTimer()

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [persistent, isPaused])

  const handleClose = () => {
    // Store dismissed toast ID in LocalUserPreferences
    LocalUserPreferences.set(`toast.dismissed.${id}`, true)
    
    setIsVisible(false)
    setTimeout(() => {
      onClose && onClose(id)
    }, 300)
  }

  const handleMouseEnter = () => {
    if (!persistent && timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      remainingTimeRef.current = remainingTimeRef.current - (Date.now() - startTimeRef.current)
      setProgressWidth((remainingTimeRef.current / duration) * 100)
      setIsPaused(true)
    }
  }

  const handleMouseLeave = () => {
    if (!persistent && isPaused) {
      startTimeRef.current = Date.now()
      setProgressWidth(0) // Resume animation to 0%
      setIsPaused(false)
    }
  }

  const getAnimationProps = () => {
    // Simplified animation that doesn't interfere with stacking positioning
    return {
      initial: { opacity: 0, scale: 0.95 },
      animate: { opacity: 1, scale: 1 },
      exit: { opacity: 0, scale: 0.95 },
      transition: { duration: 0.2, ease: 'easeOut' }
    }
  }

  if (!isVisible) return null

  const animationProps = getAnimationProps()


  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className={twMerge(styles.container, getPositionClasses(), className)}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          {...animationProps}
        >
          <div className={styles.accent} />
          
          <div className={styles.content}>
            {IconComponent && (
              <div className={styles.iconContainer}>
                <IconComponent className={styles.icon} />
              </div>
            )}
            
            <div className={styles.textContent}>
              {title && (
                <div className={styles.title}>
                  {title}
                </div>
              )}
              
              {message && (
                <div className={styles.message}>
                  {message}
                </div>
              )}
              
              {children && (
                <div className={styles.children}>
                  {children}
                </div>
              )}
            </div>
            
            <button
              onClick={handleClose}
              className={styles.closeButton}
              aria-label="Close notification"
            >
              <XMarkIcon className={styles.closeIcon} />
            </button>
          </div>
          
          {!persistent && duration > 0 && (
            <div className={styles.progressContainer}>
              <div 
                className={styles.progressBar}
                style={{
                  width: `${progressWidth}%`,
                  transition: isPaused ? 'none' : `width ${remainingTimeRef.current / 1000}s linear`
                }}
              />
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  )
}

// Global toast state management
let toasts = []
let listeners = []

const addToast = (toast) => {
  // Use existing ID if provided, otherwise generate timestamp ID
  const id = toast.id || (Date.now() + Math.random())
  const newToast = { ...toast, id }
  toasts = [...toasts, newToast]
  listeners.forEach(listener => listener(toasts))
  return id
}

const removeToastById = (id) => {
  toasts = toasts.filter(toast => toast.id !== id)
  listeners.forEach(listener => listener(toasts))
}

const clearAllToasts = () => {
  toasts = []
  listeners.forEach(listener => listener(toasts))
}

// Listen for custom events to remove specific toasts
if (typeof document !== 'undefined') {
  document.addEventListener('removeToast', (event) => {
    const { id } = event.detail
    removeToastById(id)
  })
}

// Toast Container Hook
const useToasts = () => {
  const [currentToasts, setCurrentToasts] = useState(toasts)

  useEffect(() => {
    const listener = (newToasts) => {
      setCurrentToasts([...newToasts])
    }
    listeners.push(listener)

    return () => {
      listeners = listeners.filter(l => l !== listener)
    }
  }, [])

  return currentToasts
}

// Helper function to group toasts by position for stacking
const groupToastsByPosition = (toasts) => {
  const groups = {}
  toasts.forEach(toast => {
    const position = toast.position || TOAST_POSITIONS.TOP_RIGHT
    if (!groups[position]) {
      groups[position] = []
    }
    groups[position].push(toast)
  })
  return groups
}

// Toast Container Component
export const ToastContainer = () => {
  const currentToasts = useToasts()
  const groupedToasts = groupToastsByPosition(currentToasts)


  // Only render on client side to avoid SSR issues
  if (typeof document === 'undefined') {
    return null
  }

  return createPortal(
    <>
      {Object.entries(groupedToasts).map(([position, toasts]) => (
        <div
          key={position}
          className={`fixed flex ${getStackDirection(position)}`}
          style={{
            ...getPositionContainerStyle(position),
            width: '360px', // Fixed width for all toast containers
            zIndex: 2147483647,
          }}
        >
          {toasts.map((toast, index) => (
            <div
              key={toast.id}
              style={{
                marginBottom: '12px', // Consistent margin for all toasts
              }}
            >
              <Toast
                {...toast}
                onClose={removeToastById}
                position={position}
                isStacked={toasts.length > 1}
                stackIndex={index}
                totalInStack={toasts.length}
              />
            </div>
          ))}
        </div>
      ))}
    </>,
    document.body
  )
}

// Helper function to get container positioning styles
const getPositionContainerStyle = (position) => {
  switch (position) {
    case TOAST_POSITIONS.TOP_LEFT:
      return { top: '16px', left: '16px' }
    case TOAST_POSITIONS.TOP_CENTER:
      return { top: '16px', left: '50%', transform: 'translateX(-50%)' }
    case TOAST_POSITIONS.TOP_RIGHT:
      return { top: '16px', right: '16px' }
    case TOAST_POSITIONS.BOTTOM_LEFT:
      return { bottom: '16px', left: '16px' }
    case TOAST_POSITIONS.BOTTOM_RIGHT:
      return { bottom: '16px', right: '16px' }
    default:
      return { top: '16px', right: '16px' }
  }
}

// Helper function to get flex direction based on position
const getStackDirection = (position) => {
  // Bottom positions should stack upward (newest on top)
  if (position === TOAST_POSITIONS.BOTTOM_LEFT || position === TOAST_POSITIONS.BOTTOM_RIGHT) {
    return 'flex-col-reverse'
  }
  // Top positions stack downward (newest on bottom)
  return 'flex-col'
}

// Helper function to extract text content from React elements
const extractTextContent = (element) => {
  if (typeof element === 'string' || typeof element === 'number') {
    return String(element)
  }
  
  if (element === null || element === undefined) {
    return ''
  }
  
  if (Array.isArray(element)) {
    return element.map(extractTextContent).join('')
  }
  
  if (typeof element === 'object' && element.props) {
    if (element.props.children) {
      return extractTextContent(element.props.children)
    }
    // Try other common text props
    return element.props.title || element.props.label || element.props.value || ''
  }
  
  return String(element)
}

// djb2 hash function - simple and effective for strings
const hashString = (str) => {
  let hash = 5381
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) + str.charCodeAt(i) // hash * 33 + c
  }
  return (hash >>> 0) // Convert to unsigned 32-bit integer
}

// Generate stable hash-based ID from toast content
const generateContentHash = (title, message, variant = '') => {
  const titleText = extractTextContent(title) || ''
  const messageText = extractTextContent(message) || ''
  
  // Combine title, message, and variant for uniqueness
  const content = `${titleText}|${messageText}|${variant}`
  const hash = hashString(content)
  const id = `hash_${hash}`
  
  return id
}

// Main showToast function
export const showToast = (options) => {
  const defaultOptions = {
    variant: 'default',
    duration: 5000,
    persistent: false,
    position: TOAST_POSITIONS.TOP_RIGHT,
    enableDismissalTracking: true // Enable by default - set to false to disable
  }

  const toastOptions = { ...defaultOptions, ...options }
  
  // Generate hash-based ID from content if not provided and tracking is enabled
  if (!toastOptions.id && toastOptions.enableDismissalTracking) {
    toastOptions.id = generateContentHash(toastOptions.title, toastOptions.message, toastOptions.variant)
  }
  
  // Check if this toast has been dismissed before (if it has a fixed ID)
  if (toastOptions.id) {
    const dismissalKey = `toast.dismissed.${toastOptions.id}`
    const isDismissed = LocalUserPreferences.get(dismissalKey)
    
    if (isDismissed) {
      // Toast was previously dismissed, don't show it
      return null
    }
  }
  
  if (toastOptions.duration === 0) {
    toastOptions.persistent = true
  }

  const toastId = addToast(toastOptions)
  
  return toastId
}

// Convenience methods
export const showSuccessToast = (title, message, options = {}) => 
  showToast({ ...options, title, message, variant: 'success' })

export const showErrorToast = (title, message, options = {}) => 
  showToast({ ...options, title, message, variant: 'error' })

export const showWarningToast = (title, message, options = {}) => 
  showToast({ ...options, title, message, variant: 'warning' })

export const showInfoToast = (title, message, options = {}) => 
  showToast({ ...options, title, message, variant: 'info' })

// New convenience methods with dismissal tracking enabled
export const showDismissibleToast = (title, message, options = {}) => 
  showToast({ ...options, title, message, enableDismissalTracking: true, persistent: true })

export const showDismissibleSuccessToast = (title, message, options = {}) => 
  showToast({ ...options, title, message, variant: 'success', enableDismissalTracking: true, persistent: true })

export const showDismissibleErrorToast = (title, message, options = {}) => 
  showToast({ ...options, title, message, variant: 'error', enableDismissalTracking: true, persistent: true })

export const showDismissibleWarningToast = (title, message, options = {}) => 
  showToast({ ...options, title, message, variant: 'warning', enableDismissalTracking: true, persistent: true })

export const showDismissibleInfoToast = (title, message, options = {}) => 
  showToast({ ...options, title, message, variant: 'info', enableDismissalTracking: true, persistent: true })

export const removeToast = (id) => removeToastById(id)

export { clearAllToasts }

// Utility functions for managing dismissed toasts
export const resetDismissedToasts = (pattern = 'toast.dismissed.*') => {
  LocalUserPreferences.clear(pattern)
}

export const resetSpecificDismissedToast = (id) => {
  LocalUserPreferences.delete(`toast.dismissed.${id}`)
}

export const isDismissed = (id) => {
  return LocalUserPreferences.get(`toast.dismissed.${id}`) === true
}

export const showPersistentToast = (options) => {
  // Generate a predictable ID based on content for persistent toasts
  const contentId = options.persistentId || `${options.title || ''}_${options.message || ''}`.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 50)
  
  return showToast({
    ...options,
    id: contentId,
    persistent: true
  })
}

// Debug function to inspect dismissed toasts
export const debugDismissedToasts = () => {
  // Get specifically toast dismissals
  const toastDismissals = LocalUserPreferences.get('toast.dismissed') || {}
  return toastDismissals
}

// Default export
export default Toast